import { Button, Icon, Select, Typography } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import { isString } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { DownloadReport } from '@hero/components/modals';
import { dateLabelFormatDayWithYear } from '@hero/constants';
import {
  AutomationRecipientEngagement,
  AutomationGiftSelection,
  AutomationThankYouNotes,
  AutomationWordCloud
} from '@hero/organisms';
import { useOrderReport } from '@hero/services/analytic-service/hooks';
import { useGetAcceptanceRate } from '@hero/services/order-service/hook';
import { formatDate } from '@hero/utils';

import { DATE_RANGE } from './constants';
import * as S from './style';

function AutomationAnalytics({ automation, setActiveTab }) {
  const { code } = useParams();
  const { t } = useTranslation();
  const [selectedDateRange, setSelectedDateRange] = useState(DATE_RANGE[0]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const startDate = useMemo(() => {
    if (Number(selectedDateRange.value))
      return dayjs().local().subtract(selectedDateRange.value, 'day').toISOString();
    else if (isString(selectedDateRange.value))
      return dayjs().local().startOf(selectedDateRange.value).toISOString();

    return automation?.createdAt;
  }, [selectedDateRange]);

  const endDate = useMemo(() => new Date().toISOString(), []);

  const { data: acceptanceRate } = useGetAcceptanceRate(code, {
    filters: {
      associationType: 'automation',
      startDate,
      endDate
    }
  });

  const { mutateAsync: generateReport } = useOrderReport();

  return (
    <S.AnalyticsWrapper>
      <S.Header>
        <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
          {formatDate(startDate, { format: dateLabelFormatDayWithYear, includeTime: false })} -{' '}
          {formatDate(endDate, { format: dateLabelFormatDayWithYear, includeTime: false })}
        </Typography>
        <Select
          options={DATE_RANGE}
          selectedOption={selectedDateRange}
          onChange={e => setSelectedDateRange(e)}
          size="sm"
          styles={{
            container: () => ({
              width: '160px'
            })
          }}
          block={false}
        />
        <Button
          variant="text"
          startIcon={<Icon name="download" />}
          onClick={() => setIsDownloadModalOpen(true)}
          disabled={acceptanceRate?.acceptanceRates?.total === 0}
        >
          {t('downloadReport')}
        </Button>
      </S.Header>
      <S.Row>
        <AutomationRecipientEngagement analytics={acceptanceRate} />
        <AutomationGiftSelection automation={automation} analytics={acceptanceRate} />
      </S.Row>
      <S.Row>
        <AutomationThankYouNotes analytics={acceptanceRate} setActiveTab={setActiveTab} />
        <AutomationWordCloud analytics={acceptanceRate} />
      </S.Row>
      {isDownloadModalOpen && (
        <DownloadReport
          isOpen={isDownloadModalOpen}
          onRequestClose={setIsDownloadModalOpen}
          onAsyncSubmit={async data => {
            await generateReport({
              emails: data.contacts.map(item => item.email),
              filters: {
                associationType: 'automation',
                associationCode: automation.code,
                date: {
                  createdAt: {
                    createdAtFrom: startDate,
                    createdAtTo: endDate
                  }
                }
              }
            });
          }}
          dateRange={{
            startDate,
            endDate
          }}
        />
      )}
    </S.AnalyticsWrapper>
  );
}

export { AutomationAnalytics };
